import {
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import type {
  MemberProfileModel,
  TransactionHeaderModel,
} from '../api/auth.models';
import { AuthSessionService } from './auth-session.service';
import { MemberSessionService } from './member-session.service';
import { TransactionSessionService } from './transaction-session.service';

const PREVIEW_STORAGE_KEY = 'ui.preview.enabled';
const PHYSICAL_CARD_NUMBER = '760000001';
const VIRTUAL_CARD_NUMBER = '900000000001';
const REGISTRATION_CARD_NUMBER = '760000009';

const PREVIEW_MEMBER: MemberProfileModel = {
  cell: '0820000000',
  name: 'Avery',
  surname: 'Morgan',
  email: 'member@example.test',
  birthDate: '1990-01-31',
  allowCommunication: true,
  gender: {
    id: 1,
    description: 'Female',
  },
  appMessage: 'Welcome back, Avery.',
  redeemFunctionAvailable: 'Y',
};

const PREVIEW_TRANSACTIONS: TransactionHeaderModel[] = [
  {
    id: 987654,
    merchantName: 'Orbit Market Cape Town',
    transactionDate: '2026-08-24T10:30:00Z',
    purchaseValue: 500,
    points: 25,
    pointsTypeName: 'CashBack Earned',
    operatorName: 'Till 4',
    vatAmount: 65.22,
    posTransactionId: 'POS-987654',
    vatNumber: '4123456789',
    cardholderVatNumber: '4999999999',
  },
  {
    id: 987653,
    merchantName: 'Orbit Pharmacy',
    transactionDate: '2026-08-20T14:15:00Z',
    purchaseValue: 185.5,
    points: -40,
    pointsTypeName: 'CashBack Redeemed',
    operatorName: 'Till 2',
    vatAmount: 24.2,
    posTransactionId: 'POS-987653',
  },
  {
    id: 987652,
    merchantName: 'Load Funds',
    transactionDate: '2026-08-18T09:05:00Z',
    purchaseValue: 0,
    points: 300,
    pointsTypeName: 'Load Funds',
    posTransactionId: 'POS-987652',
  },
];

@Injectable({ providedIn: 'root' })
export class UiPreviewService {
  private readonly authSession = inject(AuthSessionService);
  private readonly memberSession = inject(MemberSessionService);
  private readonly transactionSession = inject(TransactionSessionService);

  isAvailable(): boolean {
    return environment.uiPreviewEnabled;
  }

  isEnabled(): boolean {
    return this.isAvailable() && localStorage.getItem(PREVIEW_STORAGE_KEY) === 'true';
  }

  enable(): void {
    if (this.isAvailable()) {
      localStorage.setItem(PREVIEW_STORAGE_KEY, 'true');
    }
  }

  disable(): void {
    localStorage.removeItem(PREVIEW_STORAGE_KEY);
    this.authSession.clearPendingSession();
    this.memberSession.clearSession();
    this.transactionSession.clearSelectedTransaction();
  }

  initialize(): void {
    if (this.isAvailable() && this.currentRoute() === 'ui-preview') {
      this.enable();
    }

    if (!this.isEnabled()) {
      return;
    }

    this.prepareRoute(this.currentRoute());
  }

  prepareRoute(route: string): void {
    if (!this.isEnabled()) {
      return;
    }

    const normalizedRoute = route.replace(/^\//, '').split('?')[0];
    this.transactionSession.clearSelectedTransaction();

    if (
      normalizedRoute === 'ui-preview' ||
      normalizedRoute === 'card-number' ||
      normalizedRoute === 'update-app'
    ) {
      this.authSession.clearPendingSession();
      this.memberSession.clearSession();
      return;
    }

    if (normalizedRoute === 'mobile-number') {
      this.memberSession.clearSession();
      this.authSession.setPendingSession({
        action: 'confirm',
        cardNumber: PHYSICAL_CARD_NUMBER,
        isBiometric: false,
        member: this.clone(PREVIEW_MEMBER),
        virtualCard: { cardNumber: VIRTUAL_CARD_NUMBER },
        physicalCard: { cardNumber: PHYSICAL_CARD_NUMBER },
        mobileNumber: PREVIEW_MEMBER.cell,
        lastTransaction: this.previewLastTransaction(),
      });
      return;
    }

    if (normalizedRoute === 'profile-new') {
      this.memberSession.clearSession();
      this.authSession.setPendingSession({
        action: 'register',
        cardNumber: REGISTRATION_CARD_NUMBER,
        isBiometric: false,
        mobileNumber: PREVIEW_MEMBER.cell,
      });
      return;
    }

    if (normalizedRoute === 'profile-new-success') {
      this.memberSession.clearSession();
      this.authSession.setPendingSession({
        action: 'register',
        cardNumber: VIRTUAL_CARD_NUMBER,
        isBiometric: false,
        member: this.clone(PREVIEW_MEMBER),
        virtualCard: { cardNumber: VIRTUAL_CARD_NUMBER },
        physicalCard: { cardNumber: PHYSICAL_CARD_NUMBER },
        mobileNumber: PREVIEW_MEMBER.cell,
        lastTransaction: null,
      });
      return;
    }

    this.authSession.clearPendingSession();
    this.memberSession.setSession({
      cardNumber: PHYSICAL_CARD_NUMBER,
      mobileNumber: PREVIEW_MEMBER.cell ?? '',
      member: this.clone(PREVIEW_MEMBER),
      virtualCard: { cardNumber: VIRTUAL_CARD_NUMBER },
      physicalCard: { cardNumber: PHYSICAL_CARD_NUMBER },
      lastTransaction: this.previewLastTransaction(),
    });

    if (normalizedRoute.startsWith('transaction-slip')) {
      this.transactionSession.setSelectedTransaction(
        this.clone(PREVIEW_TRANSACTIONS[0])
      );
    }
  }

  handleHttpRequest(
    request: HttpRequest<unknown>,
    next: HttpHandlerFn
  ): Observable<HttpEvent<unknown>> {
    if (!this.isEnabled() || !this.isPreviewApiRequest(request.url)) {
      return next(request);
    }

    const responseBody = this.previewResponse(request);
    return of(
      new HttpResponse({
        status: 200,
        body: this.clone(responseBody),
        url: request.urlWithParams,
      })
    ).pipe(delay(80));
  }

  private currentRoute(): string {
    return window.location.pathname.split('/').filter(Boolean).join('/');
  }

  private isPreviewApiRequest(url: string): boolean {
    return url.startsWith(environment.apiEndpoint) || url.startsWith(environment.websiteApiEndpoint);
  }

  private previewResponse(request: HttpRequest<unknown>): unknown {
    if (request.url.includes('/auth/login')) {
      return { Status: 'OK' };
    }

    const url = new URL(request.urlWithParams, window.location.origin);
    const method = url.searchParams.get('method') ?? request.params.get('method');

    switch (method) {
      case 'mobileGetMinAppVersionV2':
        return { status: 'SUCCESS', minimumVersion: environment.appVersion };
      case 'mobileAuthMemberV2':
      case 'mobileValidateMemberV2':
        return this.memberResponse();
      case 'mobileCheckOTPRequiredV2':
        return { status: 'SUCCESS', required: false };
      case 'mobileRequestOtpV2':
      case 'mobileVerifyOtpV2':
      case 'mobileIncorrectCellV2':
      case 'mobileTransferCardV2':
      case 'mobileRequestOtpTransferV2':
      case 'mobileVerifyOtpTransferV2':
      case 'mobileDeleteAccountV2':
      case 'validateCardholderDetails':
        return { status: 'SUCCESS' };
      case 'mobileUpdateMemberV2':
        return { status: 'SUCCESS', member: this.clone(PREVIEW_MEMBER) };
      case 'getCardholderCountry':
        return { status: 'SUCCESS', countryId: 59 };
      case 'mobileRegisterMemberV2':
        return {
          status: 'SUCCESS',
          member: this.clone(PREVIEW_MEMBER),
          virtualCard: { cardNumber: VIRTUAL_CARD_NUMBER },
          physicalCard: { cardNumber: REGISTRATION_CARD_NUMBER },
        };
      case 'mobileGetBalancesV2':
        return {
          status: 'SUCCESS',
          cashBackBalance: 233.32,
          giftBalance: 734.32,
          currency: 'R',
          cashbackLabel: 'CASHBACK',
          loadFundLabel: 'LOAD FUNDS',
        };
      case 'mobileGetStoreCategoriesV2':
        return {
          status: 'SUCCESS',
          storeCategoryList: [
            { id: 10, categoryDescription: 'Grocery' },
            { id: 20, categoryDescription: 'Pharmacy' },
            { id: 30, categoryDescription: 'Fuel' },
          ],
        };
      case 'mobileGetStoresV2':
        return {
          status: 'SUCCESS',
          storeModelList: [
            {
              storeId: 1001,
              name: 'Orbit Market Cape Town',
              distanceKm: 2.4,
              gpsLatitude: -33.9249,
              gpsLongitude: 18.4241,
              addressLine1: '1 Market Street',
              addressLine2: 'City Centre',
              addressLine3: 'Cape Town',
              addressLine4: 'Western Cape',
              addressCode: '8001',
              tel: '0215550100',
            },
          ],
        };
      case 'mobileGetCardsForTransferV2':
        return {
          status: 'SUCCESS',
          cardModelList: [
            {
              memberModel: {
                memcardno: 760000002,
                name: 'Avery',
                surname: 'Morgan',
                cell: '0820000000',
              },
              lastTransaction: this.previewLastTransaction(),
            },
          ],
        };
      case 'getAppSpecialUrl':
        return { status: 'SUCCESS' };
      case 'mobileGetTransactionsV2':
        return { status: 'SUCCESS', transactions: this.clone(PREVIEW_TRANSACTIONS) };
      case 'mobileGetTransactionSlipLinesV2':
        return {
          status: 'SUCCESS',
          transactionLines: [
            {
              productDescription: 'Fresh produce',
              quantity: 2,
              lineItemTotalIncVat: 120,
              vatAmount: 15.65,
            },
            {
              productDescription: 'Household essentials',
              quantity: 1,
              lineItemTotalIncVat: 380,
              vatAmount: 49.57,
            },
          ],
        };
      case 'mobileOverrideRedeemLimitV2':
        return {
          status: 'SUCCESS',
          redeemOverrideMessage: 'The redeem limit was updated successfully.',
        };
      case 'mobileGetGendersV2':
        return {
          status: 'SUCCESS',
          genders: [
            { id: 1, description: 'Female' },
            { id: 2, description: 'Male' },
            { id: 3, description: 'Other' },
          ],
        };
      default:
        return {
          status: 'ERROR',
          errors: [
            {
              message: `No UI preview response is configured for ${method ?? 'this request'}.`,
            },
          ],
        };
    }
  }

  private memberResponse(): unknown {
    return {
      status: 'SUCCESS',
      otpActivated: true,
      member: this.clone(PREVIEW_MEMBER),
      virtualCard: { cardNumber: VIRTUAL_CARD_NUMBER },
      physicalCard: { cardNumber: PHYSICAL_CARD_NUMBER },
      lastTransaction: this.previewLastTransaction(),
    };
  }

  private previewLastTransaction(): { merchantName: string; transactionDate: string } {
    return {
      merchantName: PREVIEW_TRANSACTIONS[0].merchantName,
      transactionDate: PREVIEW_TRANSACTIONS[0].transactionDate,
    };
  }

  private clone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value)) as T;
  }
}

export const uiPreviewHttpInterceptor = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> =>
  inject(UiPreviewService).handleHttpRequest(request, next);
